import type { FastFeedDailyRow } from "@/api/types";
import { Panel } from "@/components/ui";
import { useChartColors } from "@/lib/theme";
import { useECharts } from "@/lib/use-echarts";
import type { EChartsOption } from "echarts";
import { useMemo } from "react";

function ChartInner({
  rows,
  cc,
}: { rows: FastFeedDailyRow[]; cc: ReturnType<typeof useChartColors> }) {
  const ticketsPerBuyer = rows.map((r) =>
    r.unique_buyers > 0 ? +(r.tickets_sold / r.unique_buyers).toFixed(2) : 0,
  );

  const option = useMemo<EChartsOption>(
    () => ({
      grid: { left: 48, right: 52, top: 32, bottom: 32 },
      legend: {
        data: ["Tickets Sold", "Tickets / Buyer"],
        textStyle: { color: cc.text, fontSize: 10 },
        top: 6,
        right: 8,
      },
      xAxis: {
        type: "category",
        data: rows.map((r) => r.date),
        axisLabel: { fontSize: 10, color: cc.text },
        axisLine: { lineStyle: { color: cc.grid } },
        axisTick: { show: false },
      },
      yAxis: [
        {
          type: "value",
          name: "Tickets",
          nameTextStyle: { fontSize: 9, color: cc.text },
          minInterval: 1,
          axisLabel: { fontSize: 10, color: cc.text },
          splitLine: { lineStyle: { color: cc.grid, type: "dashed" } },
        },
        {
          type: "value",
          name: "Ratio",
          nameTextStyle: { fontSize: 9, color: cc.text },
          axisLabel: {
            fontSize: 10,
            color: cc.text,
            formatter: (v: number) => `${v.toFixed(1)}x`,
          },
          splitLine: { show: false },
        },
      ],
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
        formatter: (p: unknown) => {
          const params = p as Array<{ seriesName: string; name: string; value: number }>;
          return `<b>${params[0].name}</b><br/>${params
            .map((s) =>
              s.seriesName === "Tickets / Buyer"
                ? `${s.seriesName}: ${(+s.value).toFixed(2)}x`
                : `${s.seriesName}: ${s.value}`,
            )
            .join("<br/>")}`;
        },
      },
      series: [
        {
          name: "Tickets Sold",
          type: "bar",
          yAxisIndex: 0,
          data: rows.map((r) => r.tickets_sold),
          barMaxWidth: 28,
          itemStyle: {
            color: {
              type: "linear",
              x: 0,
              y: 0,
              x2: 0,
              y2: 1,
              colorStops: [
                { offset: 0, color: "#34d399" },
                { offset: 1, color: "rgba(52,211,153,0.3)" },
              ],
            },
          },
        },
        {
          name: "Tickets / Buyer",
          type: "line",
          yAxisIndex: 1,
          data: ticketsPerBuyer,
          symbol: rows.length <= 5 ? "circle" : "none",
          symbolSize: 5,
          lineStyle: { color: "#e879f9", width: 2, type: "dashed" },
          itemStyle: { color: "#e879f9" },
        },
      ],
    }),
    [rows, cc, ticketsPerBuyer],
  );

  const ref = useECharts(option, [option]);
  return <div ref={ref} style={{ height: 220 }} />;
}

interface Props {
  rows: FastFeedDailyRow[];
  loading?: boolean;
}

export function FastFeedTicketsChart({ rows, loading = false }: Props) {
  const cc = useChartColors();
  return (
    <Panel title="TICKET DEMAND EFFICIENCY">
      {loading ? (
        <div className="skeleton" style={{ height: 220, borderRadius: "var(--radius-panel)" }} />
      ) : rows.length === 0 ? (
        <div
          style={{
            height: 220,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--color-text-muted)",
            fontSize: 12,
          }}
        >
          No data in this range
        </div>
      ) : (
        <ChartInner rows={rows} cc={cc} />
      )}
    </Panel>
  );
}
